/**
 * COMPLETENESS RING — anello di completezza della Smart Inbox.
 *
 * Mostra quanta parte delle informazioni attese per un messaggio è già stata
 * estratta. Il valore NON arriva dal backend: è il rapporto fra le info
 * popolate e il totale di quelle attese (popolate + mancanti), che il chiamante
 * calcola da `groupsBySection`.
 *
 * Il colore segue la soglia invece di essere sempre brand: un anello quasi
 * vuoto che si colora col primario del cliente racconterebbe la stessa cosa di
 * uno pieno. Sotto un terzo è critico, sotto due terzi è un avviso, sopra è ok.
 *
 * L'arco è disegnato con `stroke-dasharray` su una circonferenza nota: niente
 * `path` calcolati a mano, e la transizione CSS anima il riempimento da sola.
 */

interface CompletenessRingProps {
  /** Percentuale 0-100. Valori fuori scala vengono riportati nell'intervallo. */
  value: number;
  /** Diametro in px. Il tratto e il testo si ridimensionano di conseguenza. */
  size?: number;
  /** Nasconde il numero al centro (anelli molto piccoli). */
  hideValue?: boolean;
}

export function CompletenessRing({ value, size = 34, hideValue }: CompletenessRingProps) {
  const pct = Math.max(0, Math.min(100, Math.round(value)));
  // Tratto proporzionale al diametro, con un minimo che regge anche a 28px.
  const stroke = Math.max(3, Math.round(size * 0.1));
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const filled = (pct / 100) * circumference;

  const tone = pct >= 67 ? 'var(--ok)' : pct >= 34 ? 'var(--warn)' : 'var(--crit)';

  return (
    <span className="ringw" style={{ width: size, height: size }}>
      <svg width={size} height={size} aria-hidden="true">
        <circle className="ring-bg" cx={size / 2} cy={size / 2} r={radius} strokeWidth={stroke} />
        <circle
          className="ring-fg"
          cx={size / 2}
          cy={size / 2}
          r={radius}
          strokeWidth={stroke}
          stroke={tone}
          strokeDasharray={`${filled} ${circumference - filled}`}
        />
      </svg>
      {!hideValue && (
        <span className="ringv" style={{ fontSize: Math.round(size * 0.27) }}>
          {pct}
        </span>
      )}
    </span>
  );
}
